import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  InputAdornment,
  Switch,
  FormControlLabel,
  useTheme,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Language as LanguageIcon,
  Check as CheckIcon,
  SmartToy as AiIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import ApiService from '../services/api.service';
import AiProviderKeyCard from '../components/AiProviderKeyCard';
import { getRoleChipColor, getRoleLabel } from '../utils/roleUtils';
import {
  getTenantDomains,
  createTenantDomain,
  deleteTenantDomain,
  checkSubdomainAvailability
} from '../services/tenant.service';

const TypingCenterDetail = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { centerId } = useParams();

  // State
  const [typingCenter, setTypingCenter] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Only a platform admin can grant the B2B connections feature (the backend also enforces this).
  const [isPlatformAdmin] = useState(() => {
    try { return JSON.parse(localStorage.getItem('currentUser'))?.role === 'PLATFORM_ADMIN'; }
    catch { return false; }
  });
  const [connSaving, setConnSaving] = useState(false);
  const [thaklisSaving, setThaklisSaving] = useState(false);

  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [updateSaving, setUpdateSaving] = useState(false);
  const [updateFormData, setUpdateFormData] = useState({
    name: '',
    code: '',
    email: '',
    contactPerson: '',
    phone: '',
    address: '',
    organizationType: 'TYPING_CENTER'
  });

  // Subdomain State
  const [domains, setDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [openAddDomainDialog, setOpenAddDomainDialog] = useState(false);
  const [newSubdomain, setNewSubdomain] = useState('');
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);

  // Dialogs
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [userError, setUserError] = useState(null);
  const [openMappingDialog, setOpenMappingDialog] = useState(false);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [openViewUserDialog, setOpenViewUserDialog] = useState(false);
  const [openEditUserDialog, setOpenEditUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form data
  const [userFormData, setUserFormData] = useState({
    username: '', fullName: '', email: '', password: '', role: 'TC_ADMIN', roleId: null
  });
  const [editUserFormData, setEditUserFormData] = useState({
    id: '', username: '', fullName: '', email: '', role: 'TC_ADMIN', roleId: null, active: true
  });
  // Custom roles of this typing center (for the TC_STAFF role picker)
  const [tcRoles, setTcRoles] = useState([]);

  const mappedOrgIds = new Set(mappings.map(m => m.organizationId));
  const unmappedOrganizations = organizations.filter(org => !mappedOrgIds.has(org.id));

  useEffect(() => {
    fetchData();
  }, [centerId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch typing center with mappings
      const centerResponse = await ApiService.get(`/api/typing-centers/${centerId}/with-mappings`);
      if (centerResponse.data && centerResponse.data.success) {
        setTypingCenter(centerResponse.data.data);
        setMappings(centerResponse.data.data.mapped_organizations || []);
      }

      // Fetch all organizations from Thaklis API
      const orgsResponse = await ApiService.get('/api/organisations');
      if (orgsResponse.data && orgsResponse.data.success) {
        setOrganizations(orgsResponse.data.data || []);
      }

      // Fetch users for this typing center
      const usersResponse = await ApiService.get(`/api/users/typing-center/${centerId}`);
      if (usersResponse.data && usersResponse.data.success) {
        setUsers(usersResponse.data.data || []);
      }

      // Custom roles for the TC_STAFF picker (platform admin targets the TC explicitly)
      try {
        const rolesResponse = await ApiService.getRoles({ typingCenterId: centerId });
        setTcRoles(rolesResponse?.data?.data || []);
      } catch (roleErr) {
        setTcRoles([]);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load typing center details');
    } finally {
      setLoading(false);
    }
  };

  // Grant/revoke the B2B connections feature for an independent TC (platform admin only). This only
  // flips the flag — no origin connection is created, so any connection the TC makes stays breakable.
  const handleToggleConnections = async (enabled) => {
    setConnSaving(true);
    setError(null);
    try {
      await ApiService.patch(`/api/typing-centers/${centerId}/connections`, { enabled });
      setTypingCenter((prev) => ({
        ...prev,
        connections_enabled: enabled,
        can_use_connections: enabled || Boolean(prev?.promoted_from_internal_org_id),
      }));
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || 'Failed to update the connections feature');
    } finally {
      setConnSaving(false);
    }
  };

  // Turn on the full Thaklis feature set for this TC (platform admin only). One-way: there is no
  // disable — a Thaklis-enabled TC may already have pending payments, synced employees and tickets.
  const handleEnableThaklis = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Enable Thaklis features for this typing center? This cannot be undone.')) return;
    setThaklisSaving(true);
    setError(null);
    try {
      await ApiService.patch(`/api/typing-centers/${centerId}/thaklis-features`, { enabled: true });
      setTypingCenter((prev) => ({ ...prev, thaklis_features_enabled: true }));
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.message || 'Failed to enable Thaklis features');
    } finally {
      setThaklisSaving(false);
    }
  };

  const handleOpenUpdateDialog = () => {
    if (!typingCenter) return;
    setUpdateFormData({
      name: typingCenter.name || '',
      code: typingCenter.code || '',
      email: typingCenter.email || '',
      contactPerson: typingCenter.contact_person || typingCenter.contactPerson || '',
      phone:
        typingCenter.phone ||
        typingCenter.contactNumber ||
        typingCenter.contact_number ||
        '',
      address: typingCenter.address || '',
      organizationType:
        typingCenter.organization_type ||
        typingCenter.organizationType ||
        'TYPING_CENTER'
    });
    setOpenUpdateDialog(true);
  };

  const handleUpdateTypingCenter = async () => {
    try {
      setUpdateSaving(true);
      setError(null);

      const payload = {
        name: updateFormData.name,
        code: updateFormData.code,
        email: updateFormData.email,
        contact_person: updateFormData.contactPerson,
        contact_number: updateFormData.phone,
        address: updateFormData.address,
        organization_type: updateFormData.organizationType,
        active:
          typeof typingCenter?.active === 'boolean'
            ? typingCenter.active
            : typingCenter?.status
              ? String(typingCenter.status).toUpperCase() === 'ACTIVE'
              : true
      };

      const response = await ApiService.updateTypingCenter(centerId, payload);
      if (response.data && response.data.success) {
        await fetchData();
        setOpenUpdateDialog(false);
      }
    } catch (err) {
      console.error('Error updating typing center:', err);
      setError(err.response?.data?.message || 'Failed to update typing center');
    } finally {
      setUpdateSaving(false);
    }
  };

  // Create User
  const handleCreateUser = async () => {
    try {
      setLoading(true);
      setUserError(null);

      const payload = {
        username: userFormData.username,
        fullName: userFormData.fullName,
        email: userFormData.email,
        password: userFormData.password,
        role: userFormData.role,
        role_id: userFormData.role === 'TC_STAFF' ? userFormData.roleId : null,
        typingCenterId: centerId,
        createdBy: 'admin'
      };

      const response = await ApiService.post('/api/users', payload);

      if (response.data && response.data.success) {
        // Refresh data
        await fetchData();
        setOpenUserDialog(false);
        setUserFormData({ username: '', fullName: '', email: '', password: '', role: 'TC_ADMIN', roleId: null });
      }
    } catch (err) {
      console.error('Error creating user:', err);
      setUserError(err.response?.data?.error || err.response?.data?.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  // View User — open read-only dialog with the row's data
  const handleViewUser = (user) => {
    setSelectedUser(user);
    setOpenViewUserDialog(true);
  };

  // Edit User — preload the form and open the edit dialog
  const handleEditUser = (user) => {
    setEditUserFormData({
      id: user.id,
      username: user.username || '',
      fullName: user.full_name || '',
      email: user.email || '',
      role: user.role || 'TC_ADMIN',
      roleId: user.role_id || user.roleId || null,
      active: user.active !== false,
    });
    setOpenEditUserDialog(true);
  };

  // Save user edits via the admin update endpoint
  const handleUpdateUser = async () => {
    try {
      setLoading(true);
      setError(null);

      const payload = {
        username: editUserFormData.username,
        fullName: editUserFormData.fullName,
        email: editUserFormData.email,
        role: editUserFormData.role,
        role_id: editUserFormData.role === 'TC_STAFF' ? editUserFormData.roleId : null,
        active: editUserFormData.active,
      };

      const response = await ApiService.put(`/api/users/${editUserFormData.id}`, payload);

      if (response.data && response.data.success) {
        await fetchData();
        setOpenEditUserDialog(false);
      }
    } catch (err) {
      console.error('Error updating user:', err);
      setError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  // Map Organizations
  const handleMapOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);

      // Create mappings one by one
      for (const orgId of selectedOrganizations) {
        await ApiService.post('/api/mappings', {
          typingCenterId: centerId,
          organizationId: orgId,
          createdBy: 'admin'
        });
      }

      // Refresh data
      await fetchData();
      setSelectedOrganizations([]);
      setOpenMappingDialog(false);
    } catch (err) {
      console.error('Error creating mappings:', err);
      setError(err.response?.data?.message || 'Failed to create mappings');
    } finally {
      setLoading(false);
    }
  };

  // Unmap Organization
  const handleUnmapOrganization = async (mappingId) => {
    try {
      setLoading(true);
      setError(null);

      await ApiService.delete(`/api/mappings/${mappingId}`);

      // Refresh data
      await fetchData();
    } catch (err) {
      console.error('Error deleting mapping:', err);
      setError(err.response?.data?.message || 'Failed to delete mapping');
    } finally {
      setLoading(false);
    }
  };

  // Subdomain Handlers
  const fetchDomains = async () => {
    try {
      setDomainsLoading(true);
      const data = await getTenantDomains(centerId);
      setDomains(data);
    } catch (err) {
      console.error('Error fetching domains:', err);
    } finally {
      setDomainsLoading(false);
    }
  };

  useEffect(() => {
    if (centerId) fetchDomains();
  }, [centerId]);

  const handleCheckSubdomain = async () => {
    if (!newSubdomain || newSubdomain.length < 3) return;
    setCheckingSubdomain(true);
    try {
      const result = await checkSubdomainAvailability(newSubdomain.toLowerCase());
      setSubdomainAvailable(result.available);
    } catch (err) {
      setSubdomainAvailable(null);
    } finally {
      setCheckingSubdomain(false);
    }
  };

  const handleAddDomain = async () => {
    if (!newSubdomain || !subdomainAvailable) return;
    try {
      setDomainsLoading(true);
      await createTenantDomain({ typingCenterId: centerId, subdomain: newSubdomain.toLowerCase() });
      setOpenAddDomainDialog(false);
      setNewSubdomain('');
      setSubdomainAvailable(null);
      fetchDomains();
    } catch (err) {
      console.error('Error adding domain:', err);
      setError(err.response?.data?.message || 'Failed to add domain');
    } finally {
      setDomainsLoading(false);
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!window.confirm('Delete this domain?')) return;
    try {
      await deleteTenantDomain(domainId);
      fetchDomains();
    } catch (err) {
      console.error('Error deleting domain:', err);
      setError('Failed to delete domain');
    }
  };

  if (loading && !typingCenter) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!loading && !typingCenter) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6">Typing Center not found</Typography>
        <Button onClick={() => navigate('/typing-centers')} sx={{ mt: 2 }}>
          Back to Typing Centers
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {/* Header with Back Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/typing-centers')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'secondary.main' }}>
            {typingCenter.name}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<EditIcon />}
          onClick={handleOpenUpdateDialog}
          disabled={loading}
          sx={{
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 700,
            color: 'secondary.main',
            borderColor: 'primary.main',
            bgcolor: 'background.paper',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(6,77,81,0.06)' },
            '&.Mui-disabled': {
              color: 'action.disabled',
              borderColor: 'action.disabledBackground'
            }
          }}
        >
          Update Details
        </Button>
      </Box>

      {/* Typing Center Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <BusinessIcon sx={{ fontSize: 32, color: 'secondary.main', mr: 2 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
                {typingCenter.name}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {typingCenter.code}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {typingCenter.promoted_from_internal_org_id && (
                <Chip
                  label="Promoted"
                  color="secondary"
                  size="small"
                  sx={{ fontSize: '0.7rem', height: '22px', fontWeight: 700 }}
                />
              )}
              {(() => {
                const orgType = typingCenter.organization_type || typingCenter.organizationType;
                if (!orgType) return null;
                return (
                  <Chip
                    label={orgType === 'PRO' ? 'PRO' : 'TYPING CENTER'}
                    color={orgType === 'PRO' ? 'info' : 'default'}
                    size="small"
                    sx={{ fontSize: '0.7rem', height: '22px', fontWeight: 700 }}
                  />
                );
              })()}
              <Chip
                label={typingCenter.status}
                color={typingCenter.status === 'ACTIVE' ? 'success' : 'default'}
                sx={{ fontSize: '0.75rem' }}
              />
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Email
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                {typingCenter.email}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Contact Person
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                {typingCenter.contact_person || typingCenter.contactPerson || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Phone
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                {typingCenter.phone || typingCenter.contact_number || typingCenter.contactNumber || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Address
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                {typingCenter.address}
              </Typography>
            </Grid>
            {typingCenter.promoted_from_internal_org_id && (
              <Grid item xs={12}>
                <Alert severity="info" icon={false} sx={{ mt: 1, py: 0.5 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    Promoted from B2B <strong>{typingCenter.promoted_from_b2b_name || '—'}</strong>
                    {' · '}customer TC <strong>{typingCenter.promoted_from_client_name || '—'}</strong>
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* B2B Connections feature — platform-admin grant for independent (non-promoted, non-B2B) TCs */}
      {(() => {
        const orgType = typingCenter.organization_type || typingCenter.organizationType;
        // Only a standard typing center is eligible — B2B providers use it inherently, PRO is not eligible.
        if (orgType !== 'TYPING_CENTER') return null;
        const isPromoted = Boolean(typingCenter.promoted_from_internal_org_id);
        const enabled = Boolean(typingCenter.connections_enabled);
        return (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    B2B Connections
                  </Typography>
                </Box>
                {connSaving && <CircularProgress size={18} />}
              </Box>
              <Divider sx={{ mb: 2 }} />
              {isPromoted ? (
                <Alert severity="success" icon={false} sx={{ py: 0.5 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    Enabled via promotion — this TC has an unbreakable origin connection to its B2B.
                  </Typography>
                </Alert>
              ) : isPlatformAdmin ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={enabled}
                      disabled={connSaving}
                      onChange={(e) => handleToggleConnections(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Allow this typing center to connect to B2B providers
                    </Typography>
                  }
                />
              ) : (
                <Chip
                  label={enabled ? 'Connections enabled' : 'Connections disabled'}
                  color={enabled ? 'success' : 'default'}
                  size="small"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
              {!isPromoted && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontSize: '0.72rem' }}>
                  Unlike promotion, enabling this creates no connection — the TC starts with none and can freely connect to and disconnect from any B2B.
                </Typography>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Thaklis Features — platform-admin one-way enable, primarily to give a B2B provider the full
          Thaklis feature set on top of its provider role. */}
      {(() => {
        const orgType = typingCenter.organization_type || typingCenter.organizationType;
        if (orgType !== 'B2B') return null;
        const enabled = typingCenter.thaklis_features_enabled === true;
        return (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                    Thaklis Features
                  </Typography>
                </Box>
                {thaklisSaving && <CircularProgress size={18} />}
              </Box>
              <Divider sx={{ mb: 2 }} />
              {enabled ? (
                <Chip label="Thaklis features enabled" color="success" size="small" sx={{ fontSize: '0.7rem' }} />
              ) : isPlatformAdmin ? (
                <Button
                  variant="contained"
                  size="small"
                  disabled={thaklisSaving}
                  onClick={handleEnableThaklis}
                  sx={{ textTransform: 'none', fontSize: '0.875rem' }}
                >
                  Enable Thaklis Features
                </Button>
              ) : (
                <Chip label="Thaklis features disabled" color="default" size="small" sx={{ fontSize: '0.7rem' }} />
              )}
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary', fontSize: '0.72rem' }}>
                Gives this B2B the full Thaklis feature set (tickets, employee &amp; document tracking, walk-ins) so it can serve both clients and typing-center customers. This cannot be disabled once enabled.
              </Typography>
            </CardContent>
          </Card>
        );
      })()}

      {/* AI Settings — platform-admin sets this TC's AI provider API key (same tile as /ai-config). */}
      {isPlatformAdmin && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AiIcon color="secondary" />
              <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                AI Settings
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <AiProviderKeyCard typingCenterId={centerId} />
          </CardContent>
        </Card>
      )}

      {/* Subdomain Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                Subdomain
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setOpenAddDomainDialog(true)}
                sx={{ textTransform: 'none', fontSize: '0.875rem' }}
              >
                Add Subdomain
              </Button>
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {/* Subdomains List */}
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
            Subdomains
          </Typography>
          {domainsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : domains.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3, bgcolor: 'action.hover', borderRadius: 1 }}>
              <LanguageIcon sx={{ fontSize: 40, color: 'action.disabled', mb: 1 }} />
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                No subdomain configured. Click "Add Subdomain" to create one.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {domains.map((d) => (
                <Chip
                  key={d.id}
                  label={`${d.subdomain}.${process.env.REACT_APP_BASE_DOMAIN}`}
                  color={d.isPrimary ? 'primary' : 'default'}
                  onDelete={domains.length > 1 ? () => handleDeleteDomain(d.id) : undefined}
                  sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Organizations Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
              Mapped Organizations ({mappings.length})
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedOrganizations([]);
                setOpenMappingDialog(true);
              }}
              disabled={loading || unmappedOrganizations.length === 0}
              sx={{ textTransform: 'none', fontSize: '0.875rem' }}
            >
              Add Organization
            </Button>
          </Box>
          {mappings.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <BusinessIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                No organizations mapped yet. Click "Add Organization" to get started.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {mappings.map((mapping) => (
                <Grid item xs={12} sm={6} md={4} key={mapping.mappingId}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                            {mapping.organizationName || 'Organization'}
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                            {mapping.organizationCode || mapping.organizationId}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleUnmapOrganization(mapping.mappingId)}
                          disabled={loading}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box sx={{ mt: 1.5 }}>
                        <Chip
                          label={mapping.active ? 'Active' : 'Inactive'}
                          color={mapping.active ? 'success' : 'default'}
                          size="small"
                          sx={{ fontSize: '0.7rem', height: '20px' }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Users Section */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
              Users ({users.length})
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => { setUserError(null); setOpenUserDialog(true); }}
              disabled={loading}
              sx={{ textTransform: 'none', fontSize: '0.875rem' }}
            >
              Add User
            </Button>
          </Box>
          {users.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PersonIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                No users yet. Click "Add User" to get started.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'action.hover' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8125rem' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8125rem' }}>Username</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8125rem' }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8125rem' }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8125rem' }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8125rem' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell sx={{ fontSize: '0.8125rem' }}>{user.full_name}</TableCell>
                      <TableCell sx={{ fontSize: '0.8125rem' }}>{user.username}</TableCell>
                      <TableCell sx={{ fontSize: '0.8125rem' }}>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role === 'TC_STAFF' && user.role_name ? user.role_name : getRoleLabel(user.role)}
                          color={getRoleChipColor(user.role)}
                          size="small"
                          sx={{ fontSize: '0.7rem', height: '22px' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.active ? 'Active' : 'Inactive'}
                          color={user.active ? 'success' : 'default'}
                          size="small"
                          sx={{ fontSize: '0.7rem', height: '22px' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" title="View" onClick={() => handleViewUser(user)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" title="Edit" onClick={() => handleEditUser(user)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={openUpdateDialog} onClose={() => setOpenUpdateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Update Details
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Center Name"
                  value={updateFormData.name}
                  onChange={(e) => setUpdateFormData({ ...updateFormData, name: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Center Code"
                  value={updateFormData.code}
                  onChange={(e) => setUpdateFormData({ ...updateFormData, code: e.target.value })}
                  size="small"
                  placeholder="e.g., TC-DXB-002"
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.875rem' }}>Organization Type</InputLabel>
                  <Select
                    value={updateFormData.organizationType}
                    label="Organization Type"
                    onChange={(e) => setUpdateFormData({ ...updateFormData, organizationType: e.target.value })}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    <MenuItem value="TYPING_CENTER" sx={{ fontSize: '0.875rem' }}>
                      Typing Center
                    </MenuItem>
                    <MenuItem value="PRO" sx={{ fontSize: '0.875rem' }}>
                      Pro
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={updateFormData.email}
                  onChange={(e) => setUpdateFormData({ ...updateFormData, email: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={updateFormData.contactPerson}
                  onChange={(e) => setUpdateFormData({ ...updateFormData, contactPerson: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={updateFormData.phone}
                  onChange={(e) => setUpdateFormData({ ...updateFormData, phone: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={updateFormData.address}
                  onChange={(e) => setUpdateFormData({ ...updateFormData, address: e.target.value })}
                  size="small"
                  multiline
                  rows={2}
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenUpdateDialog(false)}
            sx={{ textTransform: 'none', fontSize: '0.875rem' }}
            disabled={updateSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateTypingCenter}
            variant="contained"
            disabled={!updateFormData.name || !updateFormData.code || updateSaving}
            sx={{ textTransform: 'none', fontSize: '0.875rem' }}
          >
            {updateSaving ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={openUserDialog} onClose={() => { setOpenUserDialog(false); setUserError(null); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Add User to {typingCenter.name}
        </DialogTitle>
        <DialogContent>
          {userError && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }} onClose={() => setUserError(null)}>
              {userError}
            </Alert>
          )}
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={userFormData.fullName}
                  onChange={(e) => setUserFormData({ ...userFormData, fullName: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Username"
                  value={userFormData.username}
                  onChange={(e) => setUserFormData({ ...userFormData, username: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={userFormData.password}
                  onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.875rem' }}>Role</InputLabel>
                  <Select
                    value={userFormData.role === 'TC_STAFF' && userFormData.roleId ? `ROLE:${userFormData.roleId}` : userFormData.role}
                    onChange={(e) => {
                      const v = e.target.value;
                      setUserFormData(v.startsWith('ROLE:')
                        ? { ...userFormData, role: 'TC_STAFF', roleId: v.slice(5) }
                        : { ...userFormData, role: v, roleId: null });
                    }}
                    label="Role"
                    sx={{ fontSize: '0.875rem' }}
                  >
                    <MenuItem value="TC_ADMIN" sx={{ fontSize: '0.875rem' }}>TC Admin — full access</MenuItem>
                    {tcRoles.map((r) => (
                      <MenuItem key={r.id} value={`ROLE:${r.id}`} sx={{ fontSize: '0.875rem' }}>{r.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setOpenUserDialog(false); setUserError(null); }} sx={{ textTransform: 'none', fontSize: '0.875rem' }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            disabled={!userFormData.fullName || !userFormData.username || !userFormData.email || !userFormData.password || loading}
            sx={{ textTransform: 'none', fontSize: '0.875rem' }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* View User Dialog */}
      <Dialog open={openViewUserDialog} onClose={() => setOpenViewUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>User Details</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{selectedUser.full_name || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Username</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{selectedUser.username || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{selectedUser.email || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Role</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{(selectedUser.role === 'TC_STAFF' && selectedUser.role_name) || getRoleLabel(selectedUser.role) || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{selectedUser.active ? 'Active' : 'Inactive'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Last Login</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString() : 'Never'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenViewUserDialog(false)} sx={{ textTransform: 'none', fontSize: '0.875rem' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={openEditUserDialog} onClose={() => setOpenEditUserDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={editUserFormData.fullName}
                  onChange={(e) => setEditUserFormData({ ...editUserFormData, fullName: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Username"
                  value={editUserFormData.username}
                  onChange={(e) => setEditUserFormData({ ...editUserFormData, username: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={editUserFormData.email}
                  onChange={(e) => setEditUserFormData({ ...editUserFormData, email: e.target.value })}
                  size="small"
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  inputProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.875rem' }}>Role</InputLabel>
                  <Select
                    value={editUserFormData.role === 'TC_STAFF' && editUserFormData.roleId ? `ROLE:${editUserFormData.roleId}` : editUserFormData.role}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEditUserFormData(v.startsWith('ROLE:')
                        ? { ...editUserFormData, role: 'TC_STAFF', roleId: v.slice(5) }
                        : { ...editUserFormData, role: v, roleId: null });
                    }}
                    label="Role"
                    sx={{ fontSize: '0.875rem' }}
                  >
                    <MenuItem value="TC_ADMIN" sx={{ fontSize: '0.875rem' }}>TC Admin — full access</MenuItem>
                    {tcRoles.map((r) => (
                      <MenuItem key={r.id} value={`ROLE:${r.id}`} sx={{ fontSize: '0.875rem' }}>{r.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.875rem' }}>Status</InputLabel>
                  <Select
                    value={editUserFormData.active ? 'true' : 'false'}
                    onChange={(e) => setEditUserFormData({ ...editUserFormData, active: e.target.value === 'true' })}
                    label="Status"
                    sx={{ fontSize: '0.875rem' }}
                  >
                    <MenuItem value="true" sx={{ fontSize: '0.875rem' }}>Active</MenuItem>
                    <MenuItem value="false" sx={{ fontSize: '0.875rem' }}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenEditUserDialog(false)} sx={{ textTransform: 'none', fontSize: '0.875rem' }}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateUser}
            variant="contained"
            disabled={!editUserFormData.fullName || !editUserFormData.username || !editUserFormData.email || loading}
            sx={{ textTransform: 'none', fontSize: '0.875rem' }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map Organizations Dialog */}
      <Dialog open={openMappingDialog} onClose={() => setOpenMappingDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Add Organizations to {typingCenter.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
              Available Organizations from Thaklis ({unmappedOrganizations.length})
            </Typography>
            <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {unmappedOrganizations.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                    All organizations are already mapped
                  </Typography>
                </Box>
              ) : (
                <List dense>
                  {unmappedOrganizations.map((org) => (
                    <ListItem key={org.id}>
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selectedOrganizations.includes(org.id)}
                          onChange={() => {
                            setSelectedOrganizations(prev =>
                              prev.includes(org.id)
                                ? prev.filter(id => id !== org.id)
                                : [...prev, org.id]
                            );
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={<Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{org.name}</Typography>}
                        secondary={<Typography sx={{ fontSize: '0.75rem' }}>{org.code}</Typography>}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
            {selectedOrganizations.length > 0 && (
              <Typography variant="body2" sx={{ mt: 2, fontSize: '0.875rem', color: 'secondary.main' }}>
                {selectedOrganizations.length} organization(s) selected
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenMappingDialog(false)} sx={{ textTransform: 'none', fontSize: '0.875rem' }}>
            Cancel
          </Button>
          <Button
            onClick={handleMapOrganizations}
            variant="contained"
            disabled={selectedOrganizations.length === 0}
            sx={{ textTransform: 'none', fontSize: '0.875rem' }}
          >
            Add ({selectedOrganizations.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Subdomain Dialog */}
      <Dialog open={openAddDomainDialog} onClose={() => setOpenAddDomainDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Add Subdomain</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1, color: 'text.secondary', fontSize: '0.875rem' }}>
            Create a custom subdomain for {typingCenter?.name}. Users will access the portal at this URL.
          </Typography>
          <TextField
            fullWidth
            label="Subdomain"
            value={newSubdomain}
            onChange={(e) => {
              setNewSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
              setSubdomainAvailable(null);
            }}
            onBlur={handleCheckSubdomain}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {checkingSubdomain && <CircularProgress size={20} />}
                  {subdomainAvailable === true && <CheckIcon color="success" />}
                  {subdomainAvailable === false && (
                    <Typography color="error" sx={{ fontSize: '0.75rem' }}>Taken</Typography>
                  )}
                  <Typography color="text.secondary" sx={{ ml: 1, fontSize: '0.8rem' }}>.{process.env.REACT_APP_BASE_DOMAIN}</Typography>
                </InputAdornment>
              )
            }}
            helperText={
              subdomainAvailable === false
                ? 'This subdomain is already in use'
                : 'Lowercase letters, numbers, and hyphens only'
            }
            error={subdomainAvailable === false}
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenAddDomainDialog(false)} sx={{ textTransform: 'none', fontSize: '0.875rem' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddDomain}
            disabled={!newSubdomain || !subdomainAvailable || domainsLoading}
            sx={{ textTransform: 'none', fontSize: '0.875rem' }}
          >
            {domainsLoading ? <CircularProgress size={20} /> : 'Add Subdomain'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default TypingCenterDetail;
